'use client';

import Link from 'next/link';
import { useQuery } from '@tanstack/react-query';
import { ConsentMarkdown } from '@/components/consent/consent-markdown';
import { getPublicPolicy, type PublicPolicyCode } from '@/lib/api/public-policy';

const PAGES: { code: PublicPolicyCode; href: string; label: string }[] = [
  { code: 'privacy_policy', href: '/privacy', label: '개인정보 처리방침' },
  { code: 'terms_of_service', href: '/terms', label: '이용약관' },
];

/**
 * 로그인 없이 보는 개인정보 처리방침·이용약관 한 장(사람 10-06 「첫 페이지 하단에 링크」, 법 §30② 공개).
 * 본문은 동의 화면과 같은 DB 현재 판이다. 두 문서는 위 탭 줄로 오간다(같은 틀에 내용만 바뀜).
 */
export function PublicPolicyPage({ code }: { code: PublicPolicyCode }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['public-policy', code],
    queryFn: () => getPublicPolicy(code),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  return (
    <div className="min-h-dvh bg-background">
      <header className="border-b border-border/70">
        <div className="mx-auto flex h-16 max-w-3xl items-center gap-3 px-4 sm:px-8">
          <Link href="/" className="text-sm font-semibold text-foreground focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            한채움
          </Link>
          <nav className="ml-auto flex gap-4 text-sm" aria-label="약관과 방침">
            {PAGES.map((page) => (
              <Link
                key={page.code}
                href={page.href}
                aria-current={page.code === code ? 'page' : undefined}
                className={page.code === code
                  ? 'font-medium text-foreground'
                  : 'text-muted-foreground hover:text-foreground'}
              >
                {page.label}
              </Link>
            ))}
          </nav>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-4 py-10 sm:px-8 sm:py-14">
        <h1 className="text-balance text-2xl font-semibold sm:text-3xl">
          {data?.title ?? PAGES.find((page) => page.code === code)?.label}
        </h1>
        <p className="mt-2 min-h-5 text-sm text-muted-foreground">
          {data ? `${data.publishedAt} 시행` : ''}
        </p>
        <div className="mt-8">
          {isLoading && <p className="text-sm text-muted-foreground">불러오는 중입니다.</p>}
          {isError && <p className="text-sm text-muted-foreground">본문을 불러오지 못했습니다. 잠시 뒤 다시 열어 주세요.</p>}
          {data && <ConsentMarkdown content={data.content} />}
        </div>
      </main>
    </div>
  );
}
